// ════════════════════════════════════════════════════════════════════
// src/storeVisits/visitTargets.js
//
// Admin tab module for editing monthly visit targets per store.
// Part of Store Visit Reports Phase 1 (v112.0).
//
// Endpoints:
//   GET   /api/store-visit-targets  — load all targets
//   PATCH /api/store-visit-targets  — upsert one (body: { store, monthly_target })
//
// Window re-exposure (per ELC Shift module convention — keeps inline
// HTML onclick handlers working without HTML attribute changes):
//   - window.renderVisitTargets()  — entry point, called by tab switch
//   - window.reloadVisitTargets()  — refresh after external change
//
// Re: `DB` reference (v112.0.1 fix):
//   `DB` is declared in db.js with let/const, so it lives in the shared
//   script scope but NOT on window. Use bare `DB` — esbuild leaves
//   undeclared identifiers as runtime global lookups, which resolve
//   through the script scope chain.
// ════════════════════════════════════════════════════════════════════

import { adminToken } from '../shared/admin-token.js';   // v136.12: DB token, then this tab, then the remembered copy (src/shared/admin-token.js)

function getToken() {
  return adminToken();   // v136.12: DB token, then this tab, then the remembered copy (src/shared/admin-token.js)
}

function getWorkerUrl() {
  // Bare DB, not window.DB — see header note.
  if (typeof DB !== 'undefined' && typeof DB.workerUrl === 'function') {
    return DB.workerUrl();
  }
  return '';
}

async function loadVisitTargets() {
  const token = getToken();
  if (!token) throw new Error('Not authenticated — please log in again');
  const res = await fetch(getWorkerUrl() + '/api/store-visit-targets', { cache: 'no-store',
    headers: { 'Authorization': 'Bearer ' + token },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error('Load failed: ' + res.status + ' ' + text);
  }
  return await res.json();
}

async function saveVisitTarget(store, monthlyTarget) {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await fetch(getWorkerUrl() + '/api/store-visit-targets', { cache: 'no-store',
    method: 'PATCH',
    headers: {
      'Authorization': 'Bearer ' + token,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ store: store, monthly_target: monthlyTarget }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error('Save failed: ' + res.status + ' ' + text);
  }
  return await res.json();
}

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

function renderTable(targets) {
  const rows = targets.map(function (t) {
    return (
      '<tr data-store="' + escapeHtml(t.store) + '">' +
        '<td style="padding:8px 12px;border-bottom:1px solid var(--border);">' + escapeHtml(t.store) + '</td>' +
        '<td style="padding:8px 12px;text-align:end;border-bottom:1px solid var(--border);">' +
          '<input type="number" class="svr-target-input" ' +
                 'value="' + t.monthly_target + '" ' +
                 'min="0" max="31" step="1" ' +
                 'style="width:64px;padding:4px 8px;text-align:end;" ' +
                 'data-original="' + t.monthly_target + '">' +
        '</td>' +
        '<td style="padding:8px 12px;width:140px;border-bottom:1px solid var(--border);">' +
          '<span class="svr-target-status" style="font-size:12px;color:var(--sub);"></span>' +
        '</td>' +
      '</tr>'
    );
  }).join('');

  const total = targets.reduce(function (sum, t) {
    return sum + (parseInt(t.monthly_target, 10) || 0);
  }, 0);

  return (
    '<h2 style="margin:0 0 8px;">Visit Targets</h2>' +
    '<p style="color:var(--sub);margin:0 0 16px;">' +
      'Monthly visit target per store. Edit a value and tab/click out to save. ' +
      'Total: <strong id="svrTargetTotal">' + total + '</strong> visits/month across <strong>' + targets.length + '</strong> stores.' +
    '</p>' +
    '<table style="border-collapse:collapse;width:100%;max-width:680px;background:var(--card);border:1px solid var(--border);">' +
      '<thead style="background:var(--surface-2);">' +
        '<tr>' +
          '<th style="padding:8px 12px;text-align:start;border-bottom:1px solid var(--border);">Store</th>' +
          '<th style="padding:8px 12px;text-align:end;border-bottom:1px solid var(--border);">Monthly Target</th>' +
          '<th style="padding:8px 12px;text-align:start;border-bottom:1px solid var(--border);">Status</th>' +
        '</tr>' +
      '</thead>' +
      '<tbody>' + rows + '</tbody>' +
    '</table>'
  );
}

// Sum every target input on screen and write it into the header. Kept next to
// the render that produces the number so the two cannot drift apart.
function updateTargetTotal(root) {
  const el = (root || document).querySelector('#svrTargetTotal');
  if (!el) return;
  let sum = 0;
  (root || document).querySelectorAll('.svr-target-input').forEach(function (i) {
    sum += parseInt(i.value, 10) || 0;
  });
  el.textContent = String(sum);
}

function wireUpInputs(root) {
  const inputs = root.querySelectorAll('.svr-target-input');
  inputs.forEach(function (input) {
    input.addEventListener('blur', async function () {
      const newVal = parseInt(input.value, 10);
      const oldVal = parseInt(input.dataset.original, 10);

      if (Number.isNaN(newVal) || newVal === oldVal) return;

      if (newVal < 0 || newVal > 31) {
        input.value = oldVal;
        return;
      }

      const row = input.closest('tr');
      const store = row.dataset.store;
      const statusEl = row.querySelector('.svr-target-status');

      statusEl.textContent = 'Saving…';
      statusEl.style.color = 'var(--sub)';
      input.disabled = true;

      try {
        await saveVisitTarget(store, newVal);
        input.dataset.original = String(newVal);
        // v129.32 — recompute the header total. It used to be worked out once
        // at render and then never touched, so editing a store's target saved
        // correctly but left "Total: 84 visits/month" frozen on screen. That
        // reads exactly like a hard-coded number, which is how Matt found it.
        // Summed from the live inputs rather than re-fetching: the value that
        // was just saved is already in the DOM.
        updateTargetTotal(root);
        statusEl.textContent = 'Saved';
        statusEl.style.color = 'var(--ok)';
        setTimeout(function () { statusEl.textContent = ''; }, 2000);
      } catch (e) {
        input.value = oldVal;
        statusEl.textContent = 'Save failed';
        statusEl.style.color = 'var(--ui-bad)';
        console.error('[visitTargets] save failed', e);
      } finally {
        input.disabled = false;
      }
    });
  });
}

async function renderVisitTargets() {
  const root = document.getElementById('visit-targets-root');
  if (!root) {
    console.warn('[visitTargets] mount point #visit-targets-root not found');
    return;
  }

  root.innerHTML = '<p style="color:var(--sub);">Loading targets…</p>';

  try {
    const targets = await loadVisitTargets();
    if (!Array.isArray(targets) || targets.length === 0) {
      root.innerHTML = '<p style="color:var(--sub);">No visit targets configured yet.</p>';
      return;
    }
    root.innerHTML = renderTable(targets);
    wireUpInputs(root);
  } catch (e) {
    root.innerHTML = '<p style="color:var(--ui-bad);">Error loading targets: ' + escapeHtml(e.message) + '</p>';
    console.error('[visitTargets]', e);
  }
}

// Window re-exposure (per ELC Shift module convention)
window.renderVisitTargets = renderVisitTargets;
window.reloadVisitTargets = renderVisitTargets;

export { renderVisitTargets, loadVisitTargets, saveVisitTarget };
